import { Button, Checkbox, Divider, Flex, type TableColumnsType, Tag } from 'antd';
import cn from 'classnames';
import { Avatar, type Entity, ROLE_COLOR, RoleTag } from 'components/index';
import { BASE_URL, USER_AVATAR_FOLDER } from 'config';
import { type Role, type User } from 'models/user';

import styles from './usersManagementColumns.module.css';

const ROLE_LABEL: Partial<Record<Role, string>> = {
  HEAD: 'H',
  TEAM_LEAD: 'TL',
  PARTNER: 'P',
};

type getUsersManagementColumnsParams = {
  onTeamClick: (teamId: Entity) => void;
  onManagerClick: (managerId: Entity) => void;
};

const rolesAvailableForManagement = ['PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER'] as const;

export const getUsersManagementColumns = ({
  onTeamClick,
  onManagerClick,
}: getUsersManagementColumnsParams): TableColumnsType<User> => [
  {
    dataIndex: 'avatar',
    title: 'Фото',
    align: 'center',
    width: 90,
    render: (value) => {
      const src = value ? `${BASE_URL}${USER_AVATAR_FOLDER}/${value}` : undefined;
      return <Avatar src={src} />;
    },
  },
  {
    dataIndex: 'name',
    title: 'Имя',
    sorter: true,
    width: '20%',
  },
  {
    dataIndex: 'login',
    title: 'Логин',
    width: '15%',
  },
  {
    dataIndex: 'role',
    title: 'Должность',
    width: '15%',
    align: 'center',
    filterDropdown: ({ selectedKeys, setSelectedKeys, confirm, clearFilters }) => (
      <Flex vertical className={styles.filterDropdown}>
        {rolesAvailableForManagement.map((role) => (
          <Checkbox
            key={role}
            checked={selectedKeys.includes(role)}
            onChange={(e) => {
              const checked = e.target.checked;
              const next = checked ? [...selectedKeys, role] : selectedKeys.filter((key) => key !== role);
              setSelectedKeys(next);
            }}
            className={cn(styles.filterDropdown_checkbox, {
              [styles.filterDropdown_checkbox__active]: selectedKeys.includes(role),
            })}
          >
            <RoleTag roleType={role} style={{ margin: 0 }} />
          </Checkbox>
        ))}
        <Divider className={styles.filterDropdown_divider} />
        <Flex className={styles.filterDropdown_actions} justify={'space-between'}>
          <Button
            onClick={() => clearFilters?.()}
            size={'small'}
            variant={'link'}
            color={selectedKeys.length > 0 ? 'primary' : 'default'}
          >
            Сбросить
          </Button>
          <Button onClick={() => confirm()} size={'small'} type={'primary'}>
            ОК
          </Button>
        </Flex>
      </Flex>
    ),
    filters: [
      { text: 'Хэд', value: 'HEAD' },
      { text: 'Тимлид', value: 'TEAM_LEAD' },
      { text: 'Партнер', value: 'PARTNER' },
      { text: 'Баер', value: 'BUYER' },
    ],
    defaultFilteredValue: ['HEAD', 'TEAM_LEAD', 'PARTNER', 'BUYER'],
    render: (value) => <RoleTag roleType={value} />,
  },
  {
    dataIndex: 'teams',
    title: 'Звено',
    width: '20%',
    render: (value: User['teams']) => {
      if (!value?.length) return '–';

      return value.map((team, index) => (
        <span
          key={index + team.id}
          className={styles.clickableEntity}
          onClick={() => onTeamClick({ id: team.id, name: team.name })}
        >
          {team.name}
          {index !== value.length - 1 && ', '}
        </span>
      ));
    },
  },
  {
    dataIndex: 'management',
    title: 'Руководство',
    render: (manager: User['management']) => {
      const managersCount = manager?.length || 0;

      if (!managersCount) return '–';

      return manager?.map((user, index) => (
        <p className={styles.managerLine} key={index + user.id}>
          <Tag color={ROLE_COLOR[user.role]} className={styles.shortRoleTag}>
            {ROLE_LABEL[user.role]}
          </Tag>
          <span
            className={styles.clickableEntity}
            onClick={() => onManagerClick({ id: user.id, name: user.name })}
          >
            {user.name}
          </span>
          {index !== managersCount - 1 && ', '}
        </p>
      ));
    },
  },
];
